// =============================================================================
// kioskBindPanel.jsx — the admin-only "which shop is this tablet?" panel.
//
// Renders NOTHING unless kioskBindEnabled() (host list empty today, so: never)
// AND an admin is signed in on this tablet. Both host screens mount it behind
// that same pair of conditions, so the OFF path is not "a hidden panel" — it is
// no component at all.
//
// The whole thing is one status line and at most two buttons. All the thinking
// lives in kioskBinding.js as pure functions; this file only draws.
// =============================================================================
import React from "react";
import {
  kioskBindEnabled, currentBoundSite, describeBinding, decideSiteMismatch,
  bindThisTablet, unbindThisTablet,
  kioskDurableEnabled, ensureDurableBadge, readDeviceSecret, describeDurable,
  revokeShopDeviceKeys, describeUnbind,
} from "./kioskBinding.js";

const TONE = {
  good: { bg: "rgba(212,251,111,0.10)", bd: "rgba(212,251,111,0.35)", fg: "#d4fb6f" },
  warn: { bg: "rgba(255,196,84,0.10)",  bd: "rgba(255,196,84,0.38)",  fg: "#ffc454" },
  bad:  { bg: "rgba(255,110,110,0.10)", bd: "rgba(255,110,110,0.40)", fg: "#ff6e6e" },
};

export function KioskBindPanel({ site, isAdmin }) {
  const [bound, setBound] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const shouted = React.useRef(null);   // which mismatch we have already logged
  const [hasSecret, setHasSecret] = React.useState(false);   // CL: does it hold its spare key?
  const on = kioskBindEnabled();
  const durable = kioskDurableEnabled();

  React.useEffect(() => {
    if (!on || !isAdmin) return;
    let stopped = false;
    (async () => {
      // Lane CL. On a durable host the tablet may be re-claiming its badge right
      // now with its spare key (the module kicks that off at boot). Wait for that
      // one shot to settle before reading the token, or the panel would say
      // "unbound" for a tablet that is about to be badged. On a host that is not
      // durable — every host today — this resolves immediately with null and makes
      // no call at all, so the panel behaves exactly as it does now.
      await ensureDurableBadge();
      const b = await currentBoundSite();
      if (!stopped) { setBound(b); setHasSecret(!!readDeviceSecret()); }
    })();
    return () => { stopped = true; };
  }, [on, isAdmin]);

  const siteKey = String(site?.key || "").toLowerCase();
  const siteName = site?.name || site?.short || siteKey;

  // The badge on the token vs the shop this build is set up as. Recomputed on mount
  // and after every bind/unbind, because `bound` is re-read on both. All the thinking
  // is in the pure decideSiteMismatch(); this only draws it and shouts once.
  const mismatch = (on && isAdmin)
    ? decideSiteMismatch({ boundSite: bound, siteKey, siteName })
    : null;

  // Shout once per distinct mismatch, never during render, and never at all on the
  // OFF path (the condition above is false whenever the panel is not showing).
  React.useEffect(() => {
    if (mismatch && shouted.current !== mismatch.badgedAs) {
      shouted.current = mismatch.badgedAs;
      console.error(`[kiosk site binding] ${mismatch.text}`);
    } else if (!mismatch) {
      shouted.current = null;   // fixed — let a future mismatch shout again
    }
  }, [mismatch && mismatch.badgedAs]);

  if (!on || !isAdmin) return null;

  const status = describeBinding({ boundSite: bound, siteKey, siteName });
  const tone = TONE[status.tone] || TONE.warn;
  const durableLine = describeDurable({ durable, bound, hasSecret, siteKey });

  const run = async (fn, { wasUnbind = false, wasRevoke = false } = {}) => {
    setBusy(true); setMsg(null);
    const res = await fn();
    setBound(await currentBoundSite());
    setHasSecret(!!readDeviceSecret());   // CL: re-read after a bind or an unbind
    let note = null;
    if (!res.ok) {
      note = res.error;
    } else if (wasUnbind) {
      // A call that SUCCEEDED and changed nothing has to say so. An unbind that
      // removed no rows means this shop's binding has moved to another device, and
      // every other signal on this screen reads as a clean unbind. (Review, 16 Sep.)
      const nothing = describeUnbind({ removed: res.removed, durable });
      note = nothing ? nothing.text : null;
    } else if (wasRevoke) {
      note = `Done — ${res.removed} tablet binding(s) removed at ${siteName}, ${res.passwordsKilled} spare key(s) killed. Re-bind the tills that should be badged.`;
    }
    setMsg(note);
    setBusy(false);
  };

  const btn = {
    fontFamily: "Manrope,sans-serif", fontSize: "0.78rem", fontWeight: 700,
    padding: "8px 14px", borderRadius: 999, cursor: busy ? "default" : "pointer",
    border: "1px solid rgba(212,251,111,0.4)", background: "transparent",
    color: "#d4fb6f", opacity: busy ? 0.5 : 1,
  };

  return (
    <>
    {mismatch && (
      // The loud one. A mis-bound tablet shows empty forms, an empty day sheet and an
      // empty orders strip; without this there is nothing on the screen saying why.
      <div role="alert" style={{
        margin: "10px 4px 0", padding: "12px 14px", borderRadius: 14,
        background: "rgba(255,110,110,0.16)", border: `2px solid ${TONE.bad.fg}`,
      }}>
        <div style={{
          fontFamily: "Manrope,sans-serif", fontSize: "0.62rem", fontWeight: 800,
          letterSpacing: "0.18em", textTransform: "uppercase", color: TONE.bad.fg,
        }}>
          Wrong shop
        </div>
        <div style={{ fontFamily: "Manrope,sans-serif", fontSize: "0.9rem", fontWeight: 700, color: "#fefaf0", marginTop: 4 }}>
          {mismatch.text}
        </div>
      </div>
    )}
    <div style={{
      margin: "10px 4px 0", padding: "12px 14px", borderRadius: 14,
      background: tone.bg, border: `1px solid ${tone.bd}`,
      display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12,
    }}>
      <div style={{ flex: "1 1 240px", minWidth: 200 }}>
        <div style={{
          fontFamily: "Manrope,sans-serif", fontSize: "0.62rem", fontWeight: 700,
          letterSpacing: "0.18em", textTransform: "uppercase", color: tone.fg, opacity: 0.8,
        }}>
          Tablet shop
        </div>
        <div style={{ fontFamily: "Manrope,sans-serif", fontSize: "0.85rem", color: "#fefaf0", opacity: 0.9, marginTop: 3 }}>
          {status.text}
        </div>
        {durableLine && durableLine.text && (
          // CL: on a durable host, say plainly whether this tablet will keep its
          // shop through a restart. Nothing renders on a host that is not durable.
          <div style={{
            fontFamily: "Manrope,sans-serif", fontSize: "0.76rem", marginTop: 4,
            color: (TONE[durableLine.tone] || TONE.warn).fg, opacity: 0.95,
          }}>
            {durableLine.text}
          </div>
        )}
        {msg && (
          <div style={{ fontFamily: "Manrope,sans-serif", fontSize: "0.78rem", color: TONE.bad.fg, marginTop: 5 }}>
            {msg}
          </div>
        )}
      </div>
      <button
        style={btn}
        disabled={busy}
        onClick={() => run(() => bindThisTablet(siteKey, { note: "bound from the tablet" }))}
      >
        {status.state === "bound" ? `Re-bind to ${siteName}` : `Bind this tablet to ${siteName}`}
      </button>
      {status.state !== "unbound" && (
        <button style={btn} disabled={busy} onClick={() => run(() => unbindThisTablet(), { wasUnbind: true })}>
          Unbind
        </button>
      )}
      {durable && (
        // The only thing that actually cancels a spare key once it has been used on
        // another device: unbinding reaches only the row on THIS tablet's own login.
        // Renders on a durable host only — today that is no host at all.
        <button
          style={{ ...btn, border: `1px solid ${TONE.bad.fg}`, color: TONE.bad.fg }}
          disabled={busy}
          onClick={() => run(() => revokeShopDeviceKeys(siteKey), { wasRevoke: true })}
          title={`Removes every tablet binding at ${siteName} and kills every spare key it has issued. The tills keep working; they just go back to being unbadged until they are re-bound.`}
        >
          Kill this shop&rsquo;s spare keys
        </button>
      )}
    </div>
    </>
  );
}
